import React, { createContext, useState, useEffect, useContext } from "react";
import { TOContext } from "./TContext";
import { useLocation } from "react-router-dom";
import { getForwardPaymentIntentId } from "helpers/getForwardPaymentIntentId";
import {
  useBillingPaymentIntentMutation,
  useBillingPaymentIntentUpdateMutation,
} from "api";
import { PAYMENT_SYSTEM_TYPES, usePaymentSystem } from "./PaymentSystemContext";

const PaymentContext = createContext(undefined);

export const usePaymentContext = () => {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error("usePaymentContext must be used within a PaymentProvider");
  }
  return context;
};

export const ForwardPaymentProvider = ({ children }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const bId = searchParams.get("bId");
  const oId = searchParams.get("oId");
  const { paymentSystem } = usePaymentSystem();
  const { isRefundDueShow, charges } = useContext(TOContext);
  const [getPaymentIntent, { isLoading: paymentIntentLoading }] =
    useBillingPaymentIntentMutation();
  const [isLoadingForWard, setIsLoading] = useState(paymentIntentLoading);
  const [paymentIntent, setPaymentIntent] = useState("");
  const [readyToPay, setReadyToPay] = useState(false);
  const [isForwardGooglePaySupported, setIsForwardGooglePaySupported] =
    useState(false);
  const [isForwardApplePaySupported, setIsForwardApplePaySupported] =
    useState(false);

  const [isStripeGooglePaySupported, setIsStripeGooglePaySupported] =
    useState(false);
  const [isStripeApplePaySupported, setIsStripeApplePaySupported] =
    useState(false);
  const [confirmPaymentLoading, setConfirmOrderLoading] = useState(false);

  const [forwardPaymentMethod, setForwardPaymentMethod] = useState("");
  const [forwardPaymentIntentId, setForwardPaymentIntentId] = useState("");
  const [loadingCheckoutPayment, setLoadingCheckoutPayment] = useState(true);
  const [forwardTokenizeLoading, setForwardTokenizeLoading] = useState(false);
  const [openPaymentMethods, setOpenPaymentMethods] = useState(false);
  const [isPaymentError, setPaymentError] = useState("");
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [userInfoError, setUserInfoError] = useState(false);
  const [forwardSDKLoaded, setForwardSDKLoaded] = useState(false);
  const [isApplePayOrGooglePay, setIsApplePayOrGooglePay] = useState(false);

  const [updateBillingPaymentIntent] = useBillingPaymentIntentUpdateMutation();

  // Check if Forward SDK is available
  const isForwardAvailable = () => {
    const isSecure =
      typeof window !== "undefined" &&
      window.isSecureContext &&
      window.location?.protocol === "https:";
    return (
      isSecure &&
      typeof window !== "undefined" &&
      window.Forward !== undefined &&
      typeof window.Forward === "object"
    );
  };

  useEffect(() => {
    if (
      paymentSystem !== PAYMENT_SYSTEM_TYPES.FORWARD
    ) {
      return;
    }
    const isSecure =
      typeof window !== "undefined" &&
      window.isSecureContext &&
      window.location?.protocol === "https:";
    const windowWithCardEntry = window;
    if (isSecure && windowWithCardEntry.Forward) {
      if (windowWithCardEntry.Forward.isApplePaySupported() && paymentIntent) {
        setIsForwardApplePaySupported(true);
      }
      if (windowWithCardEntry.Forward.isGooglePaySupported() && paymentIntent) {
        setIsForwardGooglePaySupported(true);
      }
    }
  }, [paymentIntent, paymentSystem]);

  const fetchPaymentIntent = async () => {
    setIsLoading(true);
    try {
      const forwardPaymentIntentId = getForwardPaymentIntentId(oId);
      const response = forwardPaymentIntentId
        ? await updateBillingPaymentIntent({
            oId: oId,
            paymentIntentId: forwardPaymentIntentId,
          })
        : await getPaymentIntent(oId);
      if (!response?.error) {
        setPaymentIntent(response?.data?.client_secret);
        if (response?.data?.id) {
          setForwardPaymentIntentId(response?.data?.id);
          localStorage.setItem(
            `forwardPaymentIntentId-${oId}`,
            response?.data?.id,
          );
        }
      }
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    } catch (error) {
      setPaymentIntent("");
      localStorage.removeItem(`forwardPaymentIntentId-${oId}`);
      console.error("Error fetching payment intent:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (
      bId &&
      oId &&
      !isRefundDueShow &&
      paymentSystem === PAYMENT_SYSTEM_TYPES.FORWARD
    ) {
      fetchPaymentIntent();
    }
  }, [bId, oId, isRefundDueShow, charges, paymentSystem]);

  return (
    <PaymentContext.Provider
      value={{
        forwardTokenizeLoading,
        setForwardTokenizeLoading,
        userInfoError,
        setUserInfoError,
        setPaymentRequest,
        paymentRequest,
        openPaymentMethods,
        setOpenPaymentMethods,
        paymentIntent,
        setPaymentIntent,
        readyToPay,
        setReadyToPay,
        isForwardGooglePaySupported,
        isForwardApplePaySupported,
        setIsForwardGooglePaySupported,
        setIsForwardApplePaySupported,
        forwardPaymentIntentId,
        setForwardPaymentIntentId,
        forwardPaymentMethod,
        setForwardPaymentMethod,
        isStripeApplePaySupported,
        setIsStripeApplePaySupported,
        setIsStripeGooglePaySupported,
        loadingCheckoutPayment,
        setLoadingCheckoutPayment,
        isPaymentError,
        setPaymentError,
        isStripeGooglePaySupported,
        forwardSDKLoaded,
        setForwardSDKLoaded,
        setIsApplePayOrGooglePay,
        isApplePayOrGooglePay,
        paymentIntentLoading,
        confirmPaymentLoading,
        setConfirmOrderLoading,
        isLoadingForWard,
        isForwardAvailable,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};

export default ForwardPaymentProvider;
